import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone } from 'lucide-react';
import { settingsApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import Layout from '@/components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

export default function TeamPage() {
  const { language } = useLanguage();
  const { settings: siteSettings } = useSiteSettings();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const response = await settingsApi.get();
      const settings = response.data;
      const teamMembers = settings.team_members || [];
      // Sort by order field
      teamMembers.sort((a, b) => (a.order || 0) - (b.order || 0));
      setMembers(teamMembers);
    } catch (error) {
      console.error('Error loading team:', error);
    } finally {
      setLoading(false);
    }
  };

  const s = siteSettings || {};
  const title = language === 'en' ? (s.team_title_en || 'Our Team') : (s.team_title || 'Unser Team');
  const subtitle = language === 'en' ? (s.team_subtitle_en || 'The People Behind Our Success') : (s.team_subtitle || 'Die Menschen hinter dem Erfolg');

  return (
    <Layout>
      <div className="min-h-screen bg-[#F9F9F7]" data-testid="team-page">
        {/* Hero Section */}
        <section className="bg-[#0A0A0A] py-16 md:py-24">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="overline text-gold mb-4"
            >
              {subtitle}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-light"
            >
              {title}
            </motion.h1>
          </div>
        </section>

        {/* Team Grid */}
        <section className="py-16 md:py-24">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-12 text-[#666666]">
                <p>{language === 'en' ? 'No team members yet.' : 'Noch keine Teammitglieder.'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {members.map((member, index) => (
                  <motion.div
                    key={member.id || index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                    data-testid={`team-member-${index}`}
                  >
                    {/* Member Image */}
                    <div className="aspect-[4/5] bg-[#E5E5E5] relative overflow-hidden">
                      {member.image_url ? (
                        <img
                          src={getImageUrl(member.image_url)}
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-6xl text-[#999999]">
                          {member.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>

                    {/* Member Info */}
                    <div className="p-6">
                      <h3 className="font-heading text-xl mb-1">{member.name}</h3>
                      <p className="text-gold text-sm mb-4">
                        {language === 'en' ? (member.position_en || member.position) : member.position}
                      </p>
                      
                      <div className="space-y-2">
                        {member.email && (
                          <a
                            href={`mailto:${member.email}`}
                            className="flex items-center gap-2 text-sm text-[#666666] hover:text-[#0A0A0A] transition-colors"
                          >
                            <Mail size={14} />
                            {member.email}
                          </a>
                        )}
                        {member.phone && (
                          <a
                            href={`tel:${member.phone}`}
                            className="flex items-center gap-2 text-sm text-[#666666] hover:text-[#0A0A0A] transition-colors"
                          >
                            <Phone size={14} />
                            {member.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
